import {
  GetMeSubscriptionPlansOptions,
  GetMeSubscriptionPlansResult,
  getMeSubscriptionPlans,
  GetMeSubscriptionPlansFields,
} from '../fetchers/me/subscription_plans';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { assignMswData, useInternalState, serializeQuery } from '../hooks/internalState';
import { useCallback } from 'react';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetMeSubscriptionPlansOptions<Fields extends GetMeSubscriptionPlansFields> =
  GetMeSubscriptionPlansOptions<Fields>;

/**
 * Get all the subscription plans that the user can upgrade to
 * This method returns every subscription plan that the authenticated user can upgrade to.
 */

export function useGetMeSubscriptionPlans<Fields extends GetMeSubscriptionPlansFields>(
  getOptions:
    | UseGetMeSubscriptionPlansOptions<Fields>
    | (() => UseGetMeSubscriptionPlansOptions<Fields> | null),
  config?: SWRConfiguration<GetMeSubscriptionPlansResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/me/subscription_plans${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getMeSubscriptionPlans({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetMeSubscriptionPlans, {
    endpoint: '/me/subscription_plans',
    method: 'GET',
  });
}

/**
 * Get all the subscription plans that the user can upgrade to
 * This method returns every subscription plan that the authenticated user can upgrade to.
 */

export function useGetMeSubscriptionPlansLazy<Fields extends GetMeSubscriptionPlansFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetMeSubscriptionPlansResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetMeSubscriptionPlansOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/subscription_plans${serializeQuery(options)}`,
          getMeSubscriptionPlans({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetMeSubscriptionPlansLazy, {
    endpoint: '/me/subscription_plans',
    method: 'GET',
  });
}
